import classNames from 'classnames';
import { useEffect } from 'react';
import { useMenu } from 'react-instantsearch';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { MENU } from '@sorare/core/src/lib/filters';

import { MyCardsRaritySwitcherBox } from '../MyCardsRaritySwitcherBox';

const StyledTabBar = styled(TabBar)`
  margin: 0 var(--unit);
`;
const filter = MENU.rarity;

type MenuItem = {
  value: string;
  label: string;
  count: number;
  isRefined: boolean;
};
const RARITIES: Rarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

type Props = {
  selectedRarity?: Rarity;
  onChange: (r: Rarity) => void;
};
export const RevealableCardRaritySwitcher = ({
  selectedRarity,
  onChange,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { items, refine } = useMenu(filter);
  const itemsOrdered = RARITIES.map(rarity =>
    items.find(item => item.value === rarity)
  ).filter(Boolean);

  const currentRarity = items.find(item => item.isRefined)?.value;
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    if (selectedRarity && !items?.some(i => i.isRefined)) {
      refine(selectedRarity);
    }
  }, [selectedRarity, items, refine]);

  const onClick = (r: Rarity) => {
    refine(r);
    onChange?.(r);
  };

  const renderRarityItem = (
    { value, count, isRefined }: MenuItem,
    key: number
  ) => {
    const rarity = value as Rarity;
    if (!isLaptopAndAbove) {
      return (
        <Tab
          key={key}
          onClick={() => onClick(rarity)}
          value={value}
          matches={['value']}
        >
          <ScarcityIcon rarity={rarity} size={2} />
          <LabelL bold>{count}</LabelL>
        </Tab>
      );
    }

    return (
      <MyCardsRaritySwitcherBox
        key={key}
        className={classNames({ active: isRefined })}
        onClick={() => onClick(rarity)}
      >
        <ScarcityIcon rarity={value as Rarity} size={5} />
        <LabelM bold>{count}</LabelM>
      </MyCardsRaritySwitcherBox>
    );
  };

  if (!isLaptopAndAbove) {
    return (
      <StyledTabBar scrollable value={currentRarity}>
        {itemsOrdered.map((item, key) => renderRarityItem(item, key))}
      </StyledTabBar>
    );
  }

  return (
    <Horizontal>
      {itemsOrdered.map((item, key) => renderRarityItem(item, key))}
    </Horizontal>
  );
};
